import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router";
import { PcFolderSetting } from "./PcFolder";
import { InstallGuide, InstallGuideLink } from "./InstallGuide";
import { Icon, Sheet } from "./ui";

/** Persönliche Einstellungen der App. Sie gelten pro Gerät und werden im Browser gespeichert. */
export interface Settings {
  accent: AccentId;
  theme: "auto" | "light" | "dark";
  /** Hintergrundton im hellen Erscheinungsbild. */
  background: BackgroundId;
  tiles: 2 | 3 | 4;
  folderView: "kacheln" | "liste";
  start: "projekte" | "notizen" | "kalender" | "zuletzt";
  motion: boolean;
  /** Vero, das Maskottchen, unten rechts anzeigen. */
  mascot: boolean;
}

export type BackgroundId = "standard" | "weiss" | "farbe" | "mint" | "himmel" | "sand" | "rose";

/** Hintergründe für das helle Erscheinungsbild. „Passend zur Farbe“ färbt den Grund leicht in der Farbwelt ein. Muster: Vorschau im Auswahlknopf. */
export const BACKGROUNDS: Record<BackgroundId, { name: string; bg: string; line: string }> = {
  standard: { name: "Standard", bg: "#F4F6F9", line: "#D4DCE6" },
  weiss: { name: "Weiß", bg: "#FFFFFF", line: "#DDE2E9" },
  farbe: { name: "Passend zur Farbe", bg: "color-mix(in srgb, var(--hero) 10%, #fff)", line: "color-mix(in srgb, var(--hero) 22%, #fff)" },
  mint: { name: "Mint", bg: "#EAF6F0", line: "#BFDDCF" },
  himmel: { name: "Himmel", bg: "#EAF2FB", line: "#C0D5EA" },
  sand: { name: "Sand", bg: "#F7F2E8", line: "#DDD2BC" },
  rose: { name: "Rosé", bg: "#FBEFEF", line: "#E6C8C8" },
};

export type AccentId = "marine" | "petrol" | "wald" | "kupfer" | "rot" | "graphit";

/** Farbwelten: Grundton (Flächen), zweiter Ton (Verlauf) und heller Akzent. */
export const ACCENTS: Record<AccentId, { name: string; hero: string; hero2: string; sky: string }> = {
  marine: { name: "Marine", hero: "#1E3863", hero2: "#2A4A80", sky: "#8EAAD8" },
  petrol: { name: "Petrol", hero: "#0E4A57", hero2: "#176A7A", sky: "#7CCAD3" },
  wald: { name: "Wald", hero: "#1D3B2D", hero2: "#2C5A44", sky: "#9ED4B1" },
  kupfer: { name: "Kupfer", hero: "#5B2A12", hero2: "#86441F", sky: "#F2B47E" },
  rot: { name: "Signal", hero: "#6E1A1A", hero2: "#9B2C26", sky: "#F59F92" },
  graphit: { name: "Graphit", hero: "#202329", hero2: "#383D46", sky: "#D4D9E0" },
};

export const DEFAULT_SETTINGS: Settings = { accent: "marine", theme: "auto", background: "standard", tiles: 3, folderView: "kacheln", start: "projekte", motion: true, mascot: true };

const KEY = "hv.app.settings";

/** Ist gerade das dunkle Erscheinungsbild aktiv (gewählt oder durch das Gerät)? */
function darkNow(theme: Settings["theme"]): boolean {
  if (theme === "dark") return true;
  if (theme === "light") return false;
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch {
    return false;
  }
}

function read(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? { ...DEFAULT_SETTINGS, ...(JSON.parse(raw) as Partial<Settings>) } : DEFAULT_SETTINGS;
  } catch {
    return DEFAULT_SETTINGS;
  }
}

const Ctx = createContext<{ settings: Settings; update: (patch: Partial<Settings>) => void }>({
  settings: DEFAULT_SETTINGS,
  update: () => undefined,
});

export function useSettings() {
  return useContext(Ctx);
}

/** Setzt Farbwelt, Hell/Dunkel und Bewegung für die ganze App. */
export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<Settings>(read);

  useEffect(() => {
    const root = document.documentElement;
    const a = ACCENTS[settings.accent] ?? ACCENTS.marine;
    root.style.setProperty("--hero", a.hero);
    root.style.setProperty("--hero-2", a.hero2);
    root.style.setProperty("--sky", a.sky);
    if (settings.theme === "auto") root.removeAttribute("data-theme");
    else root.setAttribute("data-theme", settings.theme);
    if (settings.background && settings.background !== "standard" && settings.background in BACKGROUNDS) root.setAttribute("data-bg", settings.background);
    else root.removeAttribute("data-bg");
    root.toggleAttribute("data-calm", !settings.motion);
    const meta = document.querySelector('meta[name="theme-color"]');
    const before = meta?.getAttribute("content");
    meta?.setAttribute("content", a.hero);
    return () => {
      for (const p of ["--hero", "--hero-2", "--sky"]) root.style.removeProperty(p);
      root.removeAttribute("data-theme");
      root.removeAttribute("data-bg");
      root.removeAttribute("data-calm");
      if (before) meta?.setAttribute("content", before);
    };
  }, [settings]);

  const value = useMemo(
    () => ({
      settings,
      update: (patch: Partial<Settings>) =>
        setSettings((s) => {
          const next = { ...s, ...patch };
          try {
            localStorage.setItem(KEY, JSON.stringify(next));
          } catch {
            /* Ohne Speicher gelten die Einstellungen nur bis zum Schließen. */
          }
          return next;
        }),
    }),
    [settings],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

function Choice<T extends string | number>(props: { label: string; value: T; options: [T, string][]; onChange: (v: T) => void }) {
  return (
    <fieldset className="choice">
      <legend>{props.label}</legend>
      <div>
        {props.options.map(([v, text]) => (
          <button key={String(v)} type="button" className={v === props.value ? "on" : undefined} aria-pressed={v === props.value} onClick={() => props.onChange(v)}>
            {text}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

export function SettingsSheet({ onClose }: { onClose: () => void }) {
  const { settings, update } = useSettings();
  const { pathname } = useLocation();
  const desk = pathname.startsWith("/desktop");
  const [guide, setGuide] = useState(false);
  if (guide) return <InstallGuide onClose={() => setGuide(false)} />;
  return (
    <Sheet title="Ihre App" onClose={onClose}>
      <div className="settings">
        {!desk && (
          <Link className="settings-account" to="/app/konto" onClick={onClose}>
            <Icon name="lock" />
            <span>
              <b>Mein Konto</b>
              <small>Name, Passwort, Geräte, Konto löschen</small>
            </span>
            <Icon name="chev" />
          </Link>
        )}
        {!desk && <InstallGuideLink onOpen={() => setGuide(true)} />}
        <fieldset className="choice">
          <legend>Farbwelt</legend>
          <div className="swatches">
            {(Object.keys(ACCENTS) as AccentId[]).map((id) => (
              <button
                key={id}
                type="button"
                className={id === settings.accent ? "on" : undefined}
                aria-pressed={id === settings.accent}
                onClick={() => update({ accent: id })}
                style={{ "--sw-a": ACCENTS[id].hero, "--sw-b": ACCENTS[id].sky } as React.CSSProperties}
              >
                <i aria-hidden="true" />
                <span>{ACCENTS[id].name}</span>
              </button>
            ))}
          </div>
        </fieldset>
        <Choice
          label="Erscheinungsbild"
          value={settings.theme}
          options={[
            ["auto", "Wie Gerät"],
            ["light", "Hell"],
            ["dark", "Dunkel"],
          ]}
          onChange={(theme) => update({ theme })}
        />
        <fieldset className="choice">
          <legend>Hintergrund (im hellen Erscheinungsbild)</legend>
          {darkNow(settings.theme) && <p className="muted-s">Gerade ist das dunkle Erscheinungsbild aktiv. Beim Auswählen eines Hintergrunds stellt HandVero auf „Hell“ um, damit Sie ihn sehen.</p>}
          <div className="swatches">
            {(Object.keys(BACKGROUNDS) as BackgroundId[]).map((id) => (
              <button
                key={id}
                type="button"
                className={id === settings.background ? "on" : undefined}
                aria-pressed={id === settings.background}
                onClick={() => update(id !== "standard" && darkNow(settings.theme) ? { background: id, theme: "light" } : { background: id })}
                style={{ "--sw-a": BACKGROUNDS[id].bg, "--sw-b": BACKGROUNDS[id].line } as React.CSSProperties}
              >
                <i aria-hidden="true" />
                <span>{BACKGROUNDS[id].name}</span>
              </button>
            ))}
          </div>
        </fieldset>
        <Choice
          label="Fotos pro Reihe"
          value={settings.tiles}
          options={[
            [2, "Groß"],
            [3, "Mittel"],
            [4, "Klein"],
          ]}
          onChange={(tiles) => update({ tiles })}
        />
        <Choice
          label="Ordner anzeigen als"
          value={settings.folderView}
          options={[
            ["kacheln", "Kacheln"],
            ["liste", "Liste"],
          ]}
          onChange={(folderView) => update({ folderView })}
        />
        <Choice
          label="Beim Öffnen starten mit"
          value={settings.start}
          options={[
            ["projekte", "Projekte"],
            ["zuletzt", "Letzter Ordner"],
            ["notizen", "Notizen"],
            ["kalender", "Kalender"],
          ]}
          onChange={(start) => update({ start })}
        />
        <Choice
          label="Bewegung"
          value={settings.motion ? "an" : "aus"}
          options={[
            ["an", "Lebendig"],
            ["aus", "Ruhig"],
          ]}
          onChange={(v) => update({ motion: v === "an" })}
        />
        <Choice
          label="Vero, Ihr Helfer"
          value={settings.mascot ? "an" : "aus"}
          options={[
            ["an", "Anzeigen"],
            ["aus", "Ausblenden"],
          ]}
          onChange={(v) => update({ mascot: v === "an" })}
        />
        {desk && <PcFolderSetting />}
        <p className="muted-s">Die Einstellungen gelten für dieses Gerät.</p>
      </div>
    </Sheet>
  );
}
